import { PROXIMITY_HALF_MINUTES, WALK_PACE_MPS, windowStateAt } from "@trip/domain/poi";
import type { PaletteColor } from "@trip/domain/palette";
import { Undo2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Poi, PoiCategory, ScoredPoi } from "../lib/pois.ts";
import { reason } from "../ui/index.ts";
import { PoiRow, poiName } from "./PoiRow.tsx";
import { useSnapHandle, type PoiSnap } from "./useSnapHandle.ts";

export type { PoiSnap };

/**
 * How long the ticks stay inert after a tick lands, in ms. In the collapsed sheet the next place
 * slides up into the row just ticked, its tick button exactly under the thumb that is still there;
 * a quick second tap meant for nothing would tick a place nobody has been to.
 */
export const TICK_SETTLE_MS = 400;

/**
 * Past three hours on foot a walk is not a walk: from another city it read "8286 min walk". Above
 * this the row says how far, not how long; the score is the same either way.
 */
const MAX_WALK_MINUTES = 180;

/** A span of hours as a person says it: "30 min", "1 h 20 min", "3 h". */
function span(hours: number): string {
  const mins = Math.max(1, Math.round(hours * 60));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h >= 2 || m === 0 ? `${Math.round(mins / 60)} h` : `${h} h ${m} min`;
}

/**
 * Where a place stands against its windows, in words: "Closes in 30 min", "Opens in 2 h", "Closed
 * for today…", or null when it is not time-sensitive now. The place view says the same.
 */
export function urgencySaid(
  windows: Poi["windows"],
  now: number,
): { text: string | null; passed: boolean } {
  const w = windowStateAt(now, Array.isArray(windows) ? windows : []);
  if (w.kind === "OPEN" && w.hoursLeft !== null)
    return { text: `Closes in ${span(w.hoursLeft)}`, passed: false };
  if (w.kind === "UPCOMING" && w.hoursUntil !== null)
    return { text: `Opens in ${span(w.hoursUntil)}`, passed: false };
  if (w.kind === "PASSED")
    return {
      text:
        w.hoursUntil === null
          ? "Closed for today"
          : `Closed for today, opens again in ${span(w.hoursUntil)}`,
      passed: true,
    };
  return { text: null, passed: false };
}

/**
 * How far a place is, in words: "11 min walk", "696 km away" past three hours on foot, each "from
 * map centre" instead when there is no fix. Null when there is no walk to it (it is right here, or
 * nowhere was measured from). The place view says the same.
 */
export function distanceSaid(p: ScoredPoi, fromCentre: boolean): string | null {
  // The proximity term is 1 / (1 + walk / 15), so the walk is read back out of it. With no fix it
  // was measured from the middle of the map, and "min walk" would read as from where you stand.
  if (p.proximity >= 1) return null;
  const walk = PROXIMITY_HALF_MINUTES * (1 / p.proximity - 1);
  const mins = Math.round(walk);
  if (mins > MAX_WALK_MINUTES) {
    // The walk was a straight line at a walking pace, so the distance is read back out of it.
    const km = Math.round((walk * 60 * WALK_PACE_MPS) / 1000);
    return fromCentre ? `${km} km from map centre` : `${km} km away`;
  }
  return fromCentre ? `${mins} min from map centre` : `${mins} min walk`;
}

/**
 * Why a place sits where it does, in words: the window first, because a closing window is the
 * one reason that changes by the minute, then the walk. Who likes it is the row's faces. Read off the terms
 * Task 5's score already worked out; nothing here scores anything.
 */
function why(
  p: ScoredPoi,
  now: number,
  fromCentre: boolean,
): { text: string; passed: boolean; closing: boolean } {
  const parts: string[] = [];
  const urgency = urgencySaid(p.windows, now);
  if (urgency.text) parts.push(urgency.text);
  const distance = distanceSaid(p, fromCentre);
  if (distance) parts.push(distance);
  if (p.level === null) parts.push("No safety data");
  return { text: parts.join(" · "), passed: urgency.passed, closing: p.urgency >= 2 };
}

type Write = (id: string, on: boolean) => Promise<void>;

/** One write in flight or failed, per place. */
type RowState = { busy: boolean; error: string | null };

/**
 * Layer 2's sheet: which way, and then what exactly, in what order. As the card it is the one
 * place worth walking to now; pulled up it is every place still to do, best first, with a tick and
 * a vote on each. On a phone the card stands along the bottom, under the layer switch, and the
 * full list takes the whole map; where it is placed is the page's, by `snap`.
 *
 * Two snap points and a drag between them: a tap on the handle toggles, a drag up goes to the
 * full list, a drag down back to the card, and a drag down from the card leaves it the card.
 * Nothing is ever shown done before the server says so: a tick or a vote waits for its answer,
 * and a failure leaves the row as it was and says so on the row.
 */
export function PoiSheet({
  pois,
  done = [],
  now,
  onTick,
  onVote,
  onOpen,
  onEdit,
  selected = null,
  fromCentre = false,
  snap: held,
  onSnap,
  categories,
  me = null,
  colorOf,
}: {
  /** The places still to do, best first, as Task 5 scored them. */
  pois: ScoredPoi[];
  /** The places visited, so a visit can be taken back. */
  done?: Poi[];
  /** The clock the scores were worked out for, so the reasons agree with the order. */
  now: number;
  /** Resolves once the server has the tick (or the untick); rejects when it does not. */
  onTick: Write;
  /** Resolves once the server has the vote (or its withdrawal); rejects when it does not. */
  onVote: Write;
  /** Opens the place view on a place (the phone's): its name is the way in. */
  onOpen?: ((id: string) => void) | undefined;
  /** Opens the editor on a place instead, where there is no place view (a desktop). */
  onEdit?: ((id: string) => void) | undefined;
  /**
   * A place picked on the map, where a pin does not open the place view (a desktop): the sheet
   * opens on it. A new object for every tap, so tapping the same pin again after closing the
   * sheet opens it again.
   */
  selected?: { id: string } | null;
  /** True when closeness was measured from the middle of the map, there being no fix. */
  fromCentre?: boolean;
  /**
   * Where the sheet stands, when the page keeps it (the phone's, which lays the switch out around
   * it); absent, the sheet keeps its own.
   */
  snap?: PoiSnap | undefined;
  /** Told of every move between the card and the full list. */
  onSnap?: ((snap: PoiSnap) => void) | undefined;
  /**
   * The trip's categories, when each row starts with its emoji (the phone's; 📍 for a place with
   * none). Absent, a row is the name alone, as a desktop has it.
   */
  categories?: PoiCategory[] | undefined;
  /** The reader's user id, once known: their like reads "you" and their face comes first. */
  me?: string | null;
  /** A member's colour on this trip, for their face; null for someone who has left. */
  colorOf?: ((userId: string) => PaletteColor | null) | undefined;
}) {
  const [own, setOwn] = useState<PoiSnap>("card");
  const snap = held ?? own;
  const open = snap === "full";
  const setSnap = (next: PoiSnap) => {
    if (held === undefined) setOwn(next);
    onSnap?.(next);
  };
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const sheet = useRef<HTMLElement>(null);
  const handle = useSnapHandle({ sheet, open, onSnap: setSnap });
  /**
   * The row a pin opened the sheet on, until the sheet is closed. Not `selected` itself: the page
   * keeps that, so it would mark the row, and scroll to it, on every later opening of the sheet.
   */
  const [pinned, setPinned] = useState<string | null>(null);
  /** True for `TICK_SETTLE_MS` after a tick lands: see there. */
  const [settling, setSettling] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    },
    [],
  );

  // A pin tapped on the map opens the list on its row.
  useEffect(() => {
    if (selected === null) return;
    setOwn("full");
    onSnap?.("full");
    setPinned(selected.id);
    // Once per pick: a new `onSnap` each render is not a new pick.
  }, [selected]);
  useEffect(() => {
    if (!open) setPinned(null);
  }, [open]);
  // Scrolled to once, when a pin picks it, and not again on every opening.
  useEffect(() => {
    if (pinned === null) return;
    const row = [...(sheet.current?.querySelectorAll<HTMLElement>("[data-poi]") ?? [])].find(
      (el) => el.dataset["poi"] === pinned,
    );
    row?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [pinned]);

  /** What a row's name does: opens the place view, or failing that the editor. */
  const rowOpen = onOpen
    ? { label: "Open", run: onOpen }
    : onEdit
      ? { label: "Edit", run: onEdit }
      : null;

  const shown = open ? pois : pois.slice(0, 1);

  const emojis = new Map(categories?.map((c) => [c.id, c.emoji]));
  /** A row's name, after its category's emoji where the rows show one. */
  const named = (p: Poi) => poiName(p, categories && emojis);

  async function write(id: string, run: () => Promise<void>, failed: string, tick = false) {
    setRows((r) => ({ ...r, [id]: { busy: true, error: null } }));
    try {
      await run();
      setRows((r) => ({ ...r, [id]: { busy: false, error: null } }));
      if (tick) {
        setSettling(true);
        if (settleTimer.current) clearTimeout(settleTimer.current);
        settleTimer.current = setTimeout(() => setSettling(false), TICK_SETTLE_MS);
      }
    } catch (e) {
      console.error("places: a write did not reach the server", e);
      setRows((r) => ({ ...r, [id]: { busy: false, error: `${failed} ${reason(e as Error)}` } }));
    }
  }

  const best = pois[0];
  return (
    <section
      ref={sheet}
      className={`poi-sheet${open ? " open" : ""}`}
      aria-label="Places to go"
      data-snap={snap}
    >
      <button type="button" className="poi-sheet-handle" aria-expanded={open} {...handle}>
        <span className="poi-sheet-grip" aria-hidden="true" />
        {open
          ? `${pois.length} ${pois.length === 1 ? "place" : "places"}, best first`
          : "Best place now"}
      </button>
      <div className="poi-sheet-body">
        <ol className="plain poi-rows" aria-label="Ranked places">
          {shown.map((p) => {
            const r = rows[p.id];
            const { text, passed, closing } = why(p, now, fromCentre);
            return (
              <PoiRow
                key={p.id}
                poi={p}
                name={named(p)}
                text={text}
                passed={passed}
                closing={closing}
                current={p.id === pinned}
                // Pulled up, a line of the list's own note; the place view has the whole of it.
                note={open ? p.note : null}
                error={r?.error ?? null}
                busy={r?.busy ?? false}
                settling={settling}
                open={rowOpen}
                me={me}
                colorOf={colorOf}
                onLike={() => void write(p.id, () => onVote(p.id, !p.voted), "Like not saved.")}
                onVisit={() =>
                  void write(p.id, () => onTick(p.id, true), "Not marked visited.", true)
                }
              />
            );
          })}
        </ol>
        {best === undefined && <p className="poi-row muted small">Nothing left to suggest</p>}
        {open && done.length > 0 && (
          <>
            <h3 className="label poi-done-title">Visited</h3>
            <ul className="plain poi-rows" aria-label="Visited">
              {done.map((p) => {
                const r = rows[p.id];
                return (
                  <li key={p.id} data-poi={p.id} aria-label={p.name} className="poi-row passed">
                    <div className="poi-row-text">
                      {/* Opened as any other place, where there is a place view (the phone's):
                          its comments, who ticked it and Remove are there. */}
                      {onOpen ? (
                        <button
                          type="button"
                          className="poi-row-open"
                          aria-label={`Open ${p.name}`}
                          onClick={() => onOpen(p.id)}
                        >
                          <s>{named(p)}</s>
                        </button>
                      ) : (
                        <s>{named(p)}</s>
                      )}
                      {r?.error && (
                        <span role="alert" className="error small">
                          {r.error}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="poi-tick"
                      aria-label={`Put ${p.name} back`}
                      aria-busy={r?.busy || undefined}
                      disabled={r?.busy}
                      onClick={() => void write(p.id, () => onTick(p.id, false), "Not put back.")}
                    >
                      <Undo2 size={20} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
